import * as React from "react";
import { HexColorPicker } from "react-colorful";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getContrastText, isValidHex, normalizeHex } from "@/lib/avatarColors";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

/**
 * All user-facing strings for the dialog, supplied by the caller so the
 * component stays translation-agnostic.
 */
export interface ColorPickerLabels {
  /** Dialog title */
  title: string;
  /** Description shown under the title */
  description: string;
  /** Text for the apply button */
  apply: string;
  /** Text for the cancel button */
  cancel: string;
  /** Accessible label for the hex text field */
  hex: string;
  /** Hint shown when the typed hex value is invalid */
  invalidHint: string;
}

/**
 * Props for the ColorPickerDialog component
 */
export interface ColorPickerDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback fired when the open state changes */
  onOpenChange: (open: boolean) => void;

  /**
   * The currently saved color; seeds the picker each time the dialog opens.
   * Falls back to the first preset (then near-black) when absent/invalid.
   */
  value: string | null;

  /**
   * Callback fired with a normalized `#rrggbb` color when the user applies
   * a choice. The dialog closes itself afterwards.
   */
  onApply: (hex: string) => void;

  /** User-facing strings */
  labels: ColorPickerLabels;

  /** Optional quick-select swatches shown between the picker and the hex field */
  presetColors?: readonly string[];

  /**
   * Placeholder for the hex text field
   * @default "#ef4129"
   */
  placeholder?: string;
}

/**
 * ColorPickerDialog - A modal visual hex color picker
 *
 * Combines a saturation/hue picker (react-colorful), optional quick-select
 * preset swatches, and a hex text field. The chosen color is only committed
 * via `onApply` when the user confirms, so callers keep full control of the
 * surrounding form state.
 */
export function ColorPickerDialog({
  open,
  onOpenChange,
  value,
  onApply,
  labels,
  presetColors,
  placeholder,
}: ColorPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description}</DialogDescription>
        </DialogHeader>
        <ColorPickerForm
          value={value}
          onApply={(hex) => {
            onApply(hex);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
          labels={labels}
          presetColors={presetColors}
          placeholder={placeholder}
        />
      </DialogContent>
    </Dialog>
  );
}

interface ColorPickerFormProps {
  value: string | null;
  onApply: (hex: string) => void;
  onCancel: () => void;
  labels: ColorPickerLabels;
  presetColors?: readonly string[];
  placeholder?: string;
}

// Owns the in-progress selection. Mounted fresh each time the dialog opens
// (Radix unmounts closed dialog content), so state seeding needs no effect.
function ColorPickerForm({
  value,
  onApply,
  onCancel,
  labels,
  presetColors,
  placeholder = "#ef4129",
}: ColorPickerFormProps) {
  const fallback = presetColors?.[0] ?? "#262626";
  // `draft` is always a valid normalized hex (the picker only emits valid
  // values); `text` mirrors the hex field and may transiently be invalid.
  const [draft, setDraft] = React.useState(() => normalizeHex(value ?? "") ?? fallback);
  const [text, setText] = React.useState(draft);

  const handlePick = (hex: string) => {
    const normalized = normalizeHex(hex) ?? draft;
    setDraft(normalized);
    setText(normalized);
  };

  const handleTextChange = (raw: string) => {
    setText(raw);
    const normalized = normalizeHex(raw);
    if (normalized) setDraft(normalized);
  };

  const handleApply = () => {
    const chosen = normalizeHex(text);
    if (chosen) onApply(chosen);
  };

  return (
    <>
      <div className="space-y-4">
        <HexColorPicker
          color={draft}
          onChange={handlePick}
          style={{ width: "100%" }}
          aria-label={labels.title}
        />
        <PresetSwatchRow presets={presetColors} selected={draft} onPick={handlePick} />
        <HexField
          draft={draft}
          text={text}
          onTextChange={handleTextChange}
          label={labels.hex}
          invalidHint={labels.invalidHint}
          placeholder={placeholder}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button type="button" onClick={handleApply} disabled={!isValidHex(text)}>
          {labels.apply}
        </Button>
      </DialogFooter>
    </>
  );
}

interface PresetSwatchRowProps {
  presets?: readonly string[];
  selected: string;
  onPick: (hex: string) => void;
}

function PresetSwatchRow({ presets, selected, onPick }: PresetSwatchRowProps) {
  if (!presets || presets.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((swatch) => (
        <button
          key={swatch}
          type="button"
          onClick={() => onPick(swatch)}
          aria-label={swatch}
          aria-pressed={selected === swatch}
          title={swatch}
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            selected === swatch && "ring-2 ring-ring"
          )}
          style={{ backgroundColor: swatch, color: getContrastText(swatch) }}
        >
          {selected === swatch ? <Check className="h-3.5 w-3.5" /> : null}
        </button>
      ))}
    </div>
  );
}

interface HexFieldProps {
  draft: string;
  text: string;
  onTextChange: (raw: string) => void;
  label: string;
  invalidHint: string;
  placeholder: string;
}

function HexField({ draft, text, onTextChange, label, invalidHint, placeholder }: HexFieldProps) {
  const invalid = !isValidHex(text);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="h-8 w-8 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: draft }}
        />
        <Input
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          aria-label={label}
          aria-invalid={invalid}
          placeholder={placeholder}
          maxLength={7}
          spellCheck={false}
          className="font-mono"
        />
      </div>
      {invalid ? <p className="text-xs text-destructive">{invalidHint}</p> : null}
    </div>
  );
}
